import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { GeminiLiveWidget } from './components/GeminiLiveWidget';
import { SessionDetailsView } from './components/SessionDetailsView';
import type { TabType } from './components/SessionDetailsView';
import { VoicePoweredOrb } from './components/VoicePoweredOrb';
import { Loader2, X } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { apiFetch, getApiConversationId, setApiConversationId } from './lib/api';
import type { TranscriptEntry } from './components/GeminiLiveWidget';

interface SessionData {
  api_key?: string;
  provider?: 'vertex_ai' | 'gemini_api' | 'vertex_ai_direct' | 'backend_bridge';
  transport_version?: string;
  access_token?: string;
  ws_url?: string;
  gcp_project_id?: string;
  gcp_region?: string;
  model: string;
  system_instruction: string;
  session_id?: string;
  scenario: {
    id: number;
    text: string;
    scientificScenario?: string;
    original_length: number;
    truncated: boolean;
  };
}

interface SessionResult {
  duration: number;
  scenario: {
    id: number;
    text: string;
    scientificScenario?: string;
  };
  transcript?: TranscriptEntry[];
  analysis?: any;
}

function App() {
  const { user, loading: authLoading, getToken, signInWithGoogle } = useAuth();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSessionResult, setLastSessionResult] = useState<SessionResult | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [analyzingSession, setAnalyzingSession] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [userCredits, setUserCredits] = useState<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartTimeRef = useRef<number | null>(null);

  const startSession = async () => {
    // Check if user is logged in
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    setLoading(true);
    setError(null);
    sessionIdRef.current = null;
    sessionStartTimeRef.current = null;
    const conversationId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setApiConversationId(conversationId);

    const token = await getToken();
    if (!token) {
      setError('Please sign in to start a session');
      setLoading(false);
      return;
    }

    try {
      const startRes = await apiFetch('/api/start-session', {
        method: 'POST',
        token,
        body: JSON.stringify({}),
      });
      if (!startRes.ok) {
        const errData = await startRes.json().catch(() => ({}));
        if (startRes.status === 402) {
          setError('Insufficient credits. Please buy credits.');
          setLoading(false);
          return;
        }
        throw new Error(errData.detail || 'Failed to start session');
      }
      const data: SessionData = await startRes.json();
      
      // Session ID is now returned from start-session endpoint
      sessionIdRef.current = data.session_id ?? null;
      sessionStartTimeRef.current = Date.now();
      setSessionData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      console.error('Error starting session:', err);
    } finally {
      setLoading(false);
    }
  };

  const reportUsageStop = async (durationSeconds: number) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const token = await getToken();
    if (!token) return;
    try {
      await apiFetch('/api/usage/stop', {
        method: 'POST',
        token,
        body: JSON.stringify({ session_id: sid, duration_seconds: durationSeconds }),
      });
    } catch (e) {
      console.error('Usage stop report failed:', e);
    }
    sessionIdRef.current = null;
    sessionStartTimeRef.current = null;
  };

  useEffect(() => {
    const onBeforeUnload = () => {
      const start = sessionStartTimeRef.current;
      const sid = sessionIdRef.current;
      if (sid == null || start == null) return;
      const durationSeconds = Math.floor((Date.now() - start) / 1000);
      getToken().then((token) => {
        if (!token) return;
        fetch('/api/usage/stop', {
          method: 'POST',
          body: JSON.stringify({ session_id: sid, duration_seconds: durationSeconds }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Conversation-ID': getApiConversationId(),
          },
          keepalive: true,
        }).catch(() => {});
      });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [getToken]);

  // Optional heartbeat every 30s while session is active (for reaper)
  useEffect(() => {
    if (!sessionData || !sessionIdRef.current) return;
    const interval = setInterval(() => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      getToken().then((token) => {
        if (!token) return;
        apiFetch('/api/usage/heartbeat', {
          method: 'POST',
          token,
          body: JSON.stringify({ session_id: sid }),
        }).catch(() => {});
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [sessionData, getToken]);

  const handleSessionEnd = async (duration: number, transcript?: TranscriptEntry[]) => {
    // Save session_id before reportUsageStop clears it
    const currentSessionId = sessionIdRef.current;
    await reportUsageStop(duration);
    
    if (sessionData && transcript && transcript.length > 0) {
      // Immediately show session result screen with Medical Case Details
      setLastSessionResult({
        duration,
        scenario: {
          id: sessionData.scenario.id,
          text: sessionData.scenario.text,
          scientificScenario: sessionData.scenario.scientificScenario,
        },
        transcript,
      });
      setActiveTab('details');
      setSessionData(null);
      
      // Trigger analysis in background
      setAnalyzingSession(true);
      // Run analysis asynchronously
      (async () => {
        try {
          const token = await getToken();
          if (!token) {
            throw new Error('Authentication required');
          }
          
          const response = await apiFetch('/api/analyze-session', {
            method: 'POST',
            token,
            body: JSON.stringify({
              session_id: currentSessionId,
              transcript: transcript,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to analyze session');
          }

          const result = await response.json();
          
          // Update session result with analysis
          setLastSessionResult(prev => prev ? {
            ...prev,
            analysis: result.analysis,
          } : null);
        } catch (err) {
          console.error('Failed to analyze session:', err);
          setError('Failed to analyze your session. Please try again.');
        } finally {
          setAnalyzingSession(false);
        }
      })();
    } else if (sessionData) {
      // No transcript available
      setLastSessionResult({
        duration,
        scenario: {
          id: sessionData.scenario.id,
          text: sessionData.scenario.text,
          scientificScenario: sessionData.scenario.scientificScenario,
        },
      });
      setSessionData(null);
    } else {
      setSessionData(null);
    }
  };

  const handleStartNewSession = () => {
    setLastSessionResult(null);
    setError(null);
    setActiveTab('details');
  };

  const handleLoginSuccess = async () => {
    // Close the login modal; starting a session remains an explicit user action
    setShowLoginModal(false);
  };

  // Fetch user credits when user logs in
  useEffect(() => {
    const fetchCredits = async () => {
      if (!user) {
        setUserCredits(null);
        return;
      }
      
      try {
        const token = await getToken();
        if (!token) return;
        
        const response = await apiFetch('/api/balance', {
          method: 'GET',
          token,
        });
        
        if (response.ok) {
          const data = await response.json();
          setUserCredits(data.credits || 0);
        }
      } catch (err) {
        console.error('Failed to fetch credits:', err);
      }
    };

    fetchCredits();
  }, [user, getToken]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ color: 'var(--text)' }}>
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="container mx-auto max-w-[1400px]">
        {/* Minimalist Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-1">
            <h1 className="text-3xl md:text-4xl font-semibold" style={{ color: 'var(--text)' }}>
              Arzt Training
            </h1>
            <Link
              to="/dashboard"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs md:text-sm font-medium"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: 'var(--text)',
              }}
            >
              Dashboard
            </Link>
          </div>
        </motion.div>

        {/* Main Content */}
        {lastSessionResult ? (
          /* Session Summary View */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-4xl mx-auto"
          >
            <SessionDetailsView
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              analysis={lastSessionResult.analysis}
              transcript={lastSessionResult.transcript || []}
              scenario={lastSessionResult.scenario}
              duration={lastSessionResult.duration}
              analyzing={analyzingSession}
            />

            {/* Action Button */}
            <div className="text-center mt-8">
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 12px 40px rgba(140, 150, 255, 0.35)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handleStartNewSession}
                className="btn-gradient px-10 py-4 rounded-xl text-base font-semibold mx-auto inline-block"
              >
                Start New Session
              </motion.button>
            </div>
          </motion.div>
        ) : !sessionData ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-[420px] mx-auto"
          >
            <div className="glass-card p-12 text-center">
              <div className="mb-8">
                {/* Live AI Orb Visualization */}
                <div className="w-[100px] h-[100px] mx-auto mb-7">
                  <VoicePoweredOrb
                    enableVoiceControl={false}
                    hue={0}
                    voiceSensitivity={0}
                    maxRotationSpeed={0.3}
                    maxHoverIntensity={0}
                  />
                </div>
                <h2 className="text-[26px] font-semibold mb-3" style={{ color: 'var(--text)' }}>
                  Ready to Begin?
                </h2>
                <p className="text-[15px] mb-2" style={{ color: 'var(--muted)', lineHeight: '1.5' }}>
                  Practice German medical anamnesis with an AI patient.
                </p>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mb-6 p-5 rounded-xl" style={{ 
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '2px solid rgba(239, 68, 68, 0.4)'
                  }}
                >
                  <p className="text-sm font-semibold mb-3" style={{ color: '#ef4444' }}>{error}</p>
                  {error.includes('Insufficient credits') && (
                    <Link
                      to="/dashboard"
                      className="inline-block px-4 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{
                        background: 'linear-gradient(135deg, #6ee7ff, #b892ff)',
                        color: '#fff'
                      }}
                    >
                      Buy Credits Now
                    </Link>
                  )}
                </motion.div>
              )}

              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 12px 40px rgba(140, 150, 255, 0.35)' }}
                whileTap={{ scale: 0.98 }}
                onClick={startSession}
                disabled={loading}
                className="btn-gradient w-full py-[14px] rounded-[14px] text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  'Start Roleplay'
                )}
              </motion.button>

              {/* Low credit warning */}
              {user && userCredits !== null && userCredits < 40 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 rounded-lg text-center"
                  style={{
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.3)'
                  }}
                >
                  <p className="text-xs" style={{ color: '#fbbf24' }}>
                    ⚠️ Your credits are below 40, your session might be interrupted.
                  </p>
                </motion.div>
              )}

              <p className="mt-7 text-xs" style={{ color: 'var(--muted)' }}>
                67 medizinische Fälle für die Fachsprachenprüfung Hamburg
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Scenario Info */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto p-4 rounded-xl" style={{ 
                background: 'rgba(110, 231, 255, 0.08)',
                border: '1px solid rgba(110, 231, 255, 0.2)'
              }}
            >
              <div className="flex items-center justify-center">
                <p className="text-sm font-medium" style={{ 
                  background: 'var(--accent)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Scenario ID: {sessionData.scenario.id}
                </p>
              </div>
            </motion.div>

            {/* Widget */}
            <GeminiLiveWidget
              apiKey={sessionData.api_key}
              provider={sessionData.provider}
              transportMode={sessionData.provider === 'backend_bridge' ? 'backend_mediated' : 'legacy_direct'}
              accessToken={sessionData.access_token}
              wsUrl={sessionData.ws_url}
              gcpProjectId={sessionData.gcp_project_id}
              gcpRegion={sessionData.gcp_region}
              model={sessionData.model}
              systemInstruction={sessionData.system_instruction}
              scenarioText={sessionData.scenario.text}
              onEnd={handleSessionEnd}
              initialCredits={userCredits || 0}
            />
          </motion.div>
        )}
      </div>

      {/* Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: 'rgba(0, 0, 0, 0.7)' }}
            onClick={() => setShowLoginModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-card p-8 text-center max-w-md w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowLoginModal(false)}
                className="absolute top-4 right-4 p-2 rounded-lg transition-all"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <X className="w-4 h-4" style={{ color: 'var(--text)' }} />
              </button>

              <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text)' }}>
                Login Required
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                Please sign in to start your training session
              </p>
              <button
                onClick={async () => {
                  await signInWithGoogle();
                  handleLoginSuccess();
                }}
                className="btn-gradient w-full py-3 rounded-xl font-semibold"
              >
                Sign in with Google
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
